import {
  CampaignDetail,
  CampaignOperation,
  CnpCampaignsParams,
  CspCampaignSharing,
  RejectCampaignParams,
  CampaignVerification,
  CampaignSuggestion,
  CspCampaign,
  Loa,
} from './types';
import { BulkOperationError } from '@/applications/CspPortal/Campaigns/errors';
import {
  DraftRcsCampaign,
  CnpCampaignListItem,
  RcsCampaignListItem,
  OperationErrorDetails,
  CampaignSharingStatus,
} from '@/applications/CspPortal/Campaigns/types';
import {
  globalDeleteService,
  globalGetService,
  globalPostService,
  globalPutService,
} from '@/utils/globalApiServices';
import { extractFilenameFromContentDisposition } from '@/utils/download';
import { getTimezone } from '@/utils/time';
import { Pagination } from '@/utils/types';
import { parseJson } from '../utils';

export const getCampaigns = async (
  params: Record<string, string | number | boolean> = {}
): Promise<Pagination<CspCampaign> | undefined> => {
  const response = await globalGetService('csp/campaigns', params);
  return response?.ok ? (response.data as Pagination<CspCampaign>) : undefined;
};

const mockRcsCampaigns: RcsCampaignListItem[] = [
  {
    uid: 'RABD123',
    displayName: 'AdAstra',
    brandUid: 'BH2FG34',
    brandName: 'AdAstra Inc.',
    hostingRegion: 'NORTH_AMERICA',
    billingCategory: 'SINGLE_MESSAGE',
    usecase: 'PROMOTIONAL',
    status: 'ACTIVE',
    verificationStatus: 'VERIFIED',
    upstreamCnpName: 'Demo DCA',
    createDate: new Date('06/01/2025').getTime(),
  },
  {
    uid: 'RABD456',
    displayName: 'Targe',
    brandUid: 'BH2FG35',
    brandName: 'Targe Inc.',
    hostingRegion: 'NORTH_AMERICA',
    billingCategory: 'SINGLE_MESSAGE',
    usecase: 'PROMOTIONAL',
    status: 'ACTIVE',
    verificationStatus: 'VERIFIED',
    upstreamCnpName: 'Demo DCA',
    createDate: new Date('08/10/2025').getTime(),
  },
];

export const getRcsCampaigns = async (
  // eslint-disable-next-line, @typescript-eslint/no-explicit-any
  params: Record<string, any> = {}
): Promise<Pagination<RcsCampaignListItem> | undefined> => {
  params = {
    ...params,
    channelId: 'RCS',
    timezone: getTimezone(),
  };
  const response = await globalGetService('csp/campaigns', params);
  return response?.ok
    ? (response.data as Pagination<RcsCampaignListItem>)
    : undefined;
};

export const getDraftRcsCampaigns = async (
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-explicit-any
  params: Record<string, any> = {}
): Promise<Pagination<DraftRcsCampaign> | undefined> => {
  // TODO: API is not ready yet, using mock data instead
  const mockDraftRcsCampaigns: DraftRcsCampaign[] = mockRcsCampaigns.map(
    (campaign, idx) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { uid, ...campaignWithoutUid } = campaign;
      return {
        ...campaignWithoutUid,
        draftId: `draft-${idx}`,
      };
    }
  );

  const mockPaginatedData: Pagination<DraftRcsCampaign> = {
    totalRecords: mockDraftRcsCampaigns.length,
    records: mockDraftRcsCampaigns,
    page: 1,
    recordsPerPage: 10,
  };

  return Promise.resolve(mockPaginatedData);
};

export const getCnpCampaigns = async (
  params: CnpCampaignsParams = {}
): Promise<Pagination<CnpCampaignListItem> | undefined> => {
  const response = await globalGetService('csp/campaigns/sharedWithMe', {
    ...params,
    timezone: getTimezone(),
  });
  return response?.ok
    ? (response.data as Pagination<CnpCampaignListItem>)
    : undefined;
};

export const downloadCnpCampaigns = async (query = {}) => {
  const response = await globalGetService(
    'csp/campaigns/sharedWithMe/csv',
    {
      ...query,
      timezone: getTimezone(),
    },
    'arraybuffer'
  );

  return response?.ok ? (response.data as ArrayBuffer) : undefined;
};

export const getRcsCnpCampaigns = async (
  params: Record<string, string | number | boolean | undefined> = {}
): Promise<Pagination<CnpCampaignListItem> | undefined> => {
  params = {
    ...params,
    channelId: 'RCS',
    timezone: getTimezone(),
  };
  const response = await globalGetService('csp/campaigns/sharedWithMe', params);
  return response?.ok
    ? (response.data as Pagination<CnpCampaignListItem>)
    : undefined;
};

export const downloadRcsCnpCampaigns = async (query = {}) => {
  const response = await globalGetService(
    'csp/campaigns/sharedWithMe/csv',
    {
      ...query,
      channelId: 'RCS',
      timezone: getTimezone(),
    },
    'arraybuffer'
  );

  return response?.ok ? (response.data as ArrayBuffer) : undefined;
};

export const getCampaignSharingStatus = async (
  campaignUid: string,
  params: Record<string, boolean> = {}
) => {
  const response = await globalGetService(
    `csp/campaigns/sharedByMe/${campaignUid}`,
    params
  );
  return response?.ok ? (response.data as CspCampaignSharing) : undefined;
};

export const getCampaignElectedDcas = async (
  campaignUid: string,
  params: Record<string, boolean> = {}
): Promise<number[] | undefined> => {
  const response = await globalGetService(
    `csp/campaigns/${campaignUid}/mnoIds`,
    params
  );
  return response?.ok ? (response.data as number[]) : undefined;
};

export const electCampaignWithCnp = async (
  campaignUid: string,
  upstreamCnpUid: string
): Promise<CampaignSharingStatus | undefined> => {
  const response = await globalPutService(
    `csp/campaigns/sharedByMe/${campaignUid}/with/${upstreamCnpUid}`
  );
  return response?.ok ? response.data : undefined;
};

export const rejectSharedCampaign = async (
  campaignUid: string,
  params: RejectCampaignParams
) => {
  const response = await globalDeleteService(
    `csp/campaigns/sharedWithMe/${campaignUid}`,
    params
  );
  return response?.ok ? response.data : undefined;
};

export const getCampaignDetail = async (uid: string) => {
  const response = await globalGetService(`csp/campaigns/${uid}`);
  return response?.ok ? (response.data as CampaignDetail) : undefined;
};

export const updateCampaign = async (
  campaignUid: string,
  params: Record<string, any>
) => {
  const response = await globalPutService(
    `csp/campaigns/${campaignUid}`,
    params
  );
  return response?.ok ? response.data : undefined;
};

export const deactivateCampaign = async (campaignUid: string) => {
  const response = await globalPutService(
    `csp/campaigns/${campaignUid}/deactivate`
  );
  return response?.ok ? response.data : undefined;
};

export const bulkRejectSharedCampaigns = async (
  campaignUids: string[],
  params: RejectCampaignParams
) => {
  const response = await globalDeleteService('csp/campaigns/bulkSharedWithMe', {
    campaignUid: campaignUids,
    ...params,
  });
  if (response?.ok) {
    return response.data;
  } else if (response?.data.error && !response?.data.code) {
    throw new BulkOperationError(
      response.data.error as Record<string, OperationErrorDetails>
    );
  }
  return undefined;
};

export const getCampaignOperations = async (campaignUid: string) => {
  const response = await globalGetService(
    `csp/campaigns/${campaignUid}/operations`
  );
  if (response?.ok && Array.isArray(response.data?.records)) {
    return response.data.records.map((d) => {
      const {
        rulesEngineResultsJson,
        osrAttributesJson,
        checklistJson,
        ...restProps
      } = d;
      return {
        ...restProps,
        rulesEngineResultsJson: parseJson(rulesEngineResultsJson),
        osrAttributesJson: parseJson(osrAttributesJson),
        checklistJson: parseJson(checklistJson),
      };
    }) as CampaignOperation[];
  }
  return response?.ok ? response.data : [];
};

export const nudgeUpstreamCnp = async (
  campaignUid: string,
  params: { intent: string; description: string }
) => {
  const response = await globalPostService(
    `csp/campaigns/${campaignUid}/nudge`,
    params
  );
  return response?.ok ? (response.data as { message: string }) : undefined;
};

export const getExcludedMnos = async (
  campaignUid: string,
  configs?: Record<string, any>
): Promise<number[]> => {
  const response = await globalGetService(
    `csp/campaigns/${campaignUid}/excludedMno`,
    {},
    configs
  );
  return response?.ok ? response.data : [];
};

export const deleteNetwork = async (
  campaignUid: string,
  networkId: number,
  params: { explanation: string }
) => {
  const response = await globalDeleteService(
    `csp/campaigns/${campaignUid}/operations/network/${networkId}`,
    params
  );
  return response?.ok ? true : undefined;
};

export const getVerificationRecords = async (
  campaignUid: string,
  params?: Record<string, any>,
  configs?: Record<string, any>
) => {
  const response = await globalGetService(
    `csp/campaigns/${campaignUid}/verification`,
    params,
    configs
  );

  const normalize = (data: any): CampaignVerification => {
    if (!!data && typeof data === 'object') {
      return {
        ...data,
        fullReport: parseJson(data.fullReport),
        reasons: parseJson(data.reasons),
      };
    }
    return data;
  };

  if (!response?.ok || !response?.data) {
    return [];
  }

  return response.data.map(normalize) as CampaignVerification[];
};

export const getCampaignSuggestions = async (
  params?: Partial<{
    status: string;
    channelId: string;
    limit: number;
    fallbackAssociation: boolean;
    brandUid: string;
    campaignUidPrefix: string;
  }>,
  configs?: Record<string, any>
) => {
  const response = await globalGetService(
    'csp/campaigns/suggestions',
    params,
    configs
  );
  return response?.ok ? (response.data as CampaignSuggestion[]) : [];
};

export const deleteRcsFallbackCampaign = async (campaignUid: string) => {
  const response = await globalDeleteService(
    `csp/campaigns/${campaignUid}/fallback`
  );
  return response?.ok ? ((response.data || true) as unknown) : undefined;
};

export const downloadCspCampaigns = async (query = {}) => {
  const response = await globalGetService(
    'csp/campaigns/csv',
    {
      ...query,
      timezone: getTimezone(),
    },
    'arraybuffer'
  );

  return response?.ok ? (response.data as ArrayBuffer) : undefined;
};

export const getCampaignLoas = async (campaignUid: string) => {
  const response = await globalGetService(`csp/campaigns/${campaignUid}/LOA`);
  return (response?.ok ? response.data : []) as Loa[];
};

export const getCampaignSenderId = async (campaignUid: string) => {
  const response = await globalGetService(
    `csp/campaigns/${campaignUid}/senderId`
  );
  return response?.ok ? response.data : undefined;
};

export const downloadLoa = async (campaignUid: string, docUid: string) => {
  const response = await globalGetService(
    `csp/campaigns/${campaignUid}/LOA/${docUid}`,
    { format: 'pdf' },
    { responseType: 'arraybuffer' }
  );
  if (response?.ok) {
    const filename = extractFilenameFromContentDisposition(
      response.headers?.['content-disposition'],
      'LOA.pdf'
    );
    return {
      data: response.data as ArrayBuffer,
      filename,
    };
  }
  return undefined;
};

export const bulkSuspendCampaigns = async (
  campaignUids: string[],
  suspensionCategory: string,
  explanation: string
) => {
  const response = await globalPostService('csp/campaigns/bulkSuspension', {
    campaignUid: campaignUids,
    suspensionCategory,
    explanation,
  });
  if (response?.ok) {
    return true;
  } else if (response?.data) {
    const details: Record<string, OperationErrorDetails> = {};
    Object.entries(response.data as Record<string, string>).forEach(
      ([key, value]) => {
        try {
          [details[key]] = JSON.parse(value) as OperationErrorDetails[];
        } catch {
          details[key] = {
            code: -1,
            description: 'undefined',
          };
        }
      }
    );
    throw new BulkOperationError(details);
  }
};

export const bulkLiftSuspensions = async (
  campaignUids: string[],
  explanation: string
) => {
  const response = await globalDeleteService('csp/campaigns/bulkSuspension', {
    campaignUid: campaignUids,
    explanation,
  });
  if (response?.ok) {
    return true;
  } else if (response?.data) {
    const details: Record<string, OperationErrorDetails> = {};
    Object.entries(response.data as Record<string, string>).forEach(
      ([key, value]) => {
        try {
          [details[key]] = JSON.parse(value) as OperationErrorDetails[];
        } catch {
          details[key] = {
            code: -1,
            description: 'undefined',
          };
        }
      }
    );
    throw new BulkOperationError(details);
  }
};
